import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useSupabase, useTenant } from "@how/app-kit";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

const schema = z.object({
  household: z.array(
    z.object({ name: z.string(), relationship: z.string().nullable(), source: z.string() }),
  ),
  named: z.array(
    z.object({ id: z.string(), name: z.string(), relationship: z.string().nullable() }),
  ),
});
export type PersonCollectors = z.infer<typeof schema>;

/** Who may collect a child (#174): household adults by default, and the people the church named. ADR 0028 applies. */
export function usePersonCollectors(personId: string, enabled: boolean) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["person-collectors", tenantId, personId],
    enabled: enabled && tenantId !== null,
    retry: false,
    queryFn: async (): Promise<PersonCollectors> => {
      const { data, error } = await supabase.rpc("person_collectors", {
        p_tenant_id: tenantId ?? "",
        p_person_id: personId,
      });
      if (error) throw error;
      return schema.parse(data);
    },
  });
}

function useInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return (personId: string) =>
    client.invalidateQueries({ queryKey: ["person-collectors", tenantId, personId] });
}

export function useAddCollector() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useInvalidation();
  return useMutation({
    mutationFn: async (input: { personId: string; name: string; relationship: string }) => {
      const { error } = await supabase.rpc("set_authorized_collector", {
        p_tenant_id: tenantId ?? "",
        p_person_id: input.personId,
        p_name: input.name,
        ...(input.relationship.trim() === "" ? {} : { p_relationship: input.relationship }),
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.personId),
  });
}

export function useRemoveCollector() {
  const supabase = useSupabase();
  const invalidate = useInvalidation();
  return useMutation({
    mutationFn: async (input: { personId: string; collectorId: string }) => {
      const { error } = await supabase.rpc("delete_authorized_collector", {
        p_collector_id: input.collectorId,
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.personId),
  });
}
